import type { BffPcGlobalData } from '@/api/types/common';
import type { MagicJumpItem } from '@/api/types/magic-jump';
import type {
  NavBadge,
  NavFirstLevelItem,
  NavResourceSlot,
  NavSecondLevelItem,
  NavThirdLevelItem,
} from '@/api/types/nav';

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function str(v: unknown): string {
  if (v === null || v === undefined) {
    return '';
  }
  return String(v);
}

/** 与 `components/layout/nav.json`（BFF block 302）一致 */
function normalizeBadge(raw: unknown): NavBadge | null {
  if (!isRecord(raw)) {
    return null;
  }
  if (raw.badge_type === 'image') {
    const badge_image = str(raw.badge_image);
    if (!badge_image) {
      return null;
    }
    return {
      badge_type: 'image',
      badge_image,
      alt: raw.image_alt != null ? str(raw.image_alt) : undefined,
    };
  }
  if (raw.badge_type === 'text') {
    const text = str(raw.text);
    if (!text) {
      return null;
    }
    return {
      badge_type: 'text',
      text,
      text_color: raw.text_color != null ? str(raw.text_color) : undefined,
      fill_color: raw.fill_color != null ? str(raw.fill_color) : undefined,
    };
  }
  return null;
}

function normalizeJumpTarget(raw: unknown): MagicJumpItem | null {
  if (!isRecord(raw)) {
    return null;
  }
  if (raw.jump_type === undefined || raw.jump_type === null) {
    return null;
  }
  const jump_type = Number(raw.jump_type) as MagicJumpItem['jump_type'];
  const Jump_target = raw.Jump_target;
  const item: MagicJumpItem = { jump_type };
  if (Jump_target !== undefined && Jump_target !== null) {
    item.Jump_target = Jump_target as string | number;
  }
  return item;
}

function normalizeThirdLevel(raw: Record<string, unknown>, index: number): NavThirdLevelItem {
  const nav_title = str(raw.nav_title);
  const nav_title_color = raw.nav_title_color != null ? str(raw.nav_title_color) : undefined;
  const thirdImg = raw.third_level_image;
  const third_level_image = thirdImg !== undefined && thirdImg !== null ? str(thirdImg) : undefined;
  const alt = raw.image_alt != null ? str(raw.image_alt) : undefined;

  return {
    nav_title,
    nav_title_color,
    third_level_image: third_level_image || undefined,
    jump_target: normalizeJumpTarget(raw.jump_target),
    badge: normalizeBadge(raw.badge),
    sort: index + 1,
    alt,
  };
}

function normalizeSecondLevel(raw: Record<string, unknown>, index: number): NavSecondLevelItem {
  const dataList = raw.data_list;
  const list: NavThirdLevelItem[] = Array.isArray(dataList)
    ? dataList.map((row, i) => normalizeThirdLevel(isRecord(row) ? row : {}, i))
    : [];

  const display_style = raw.display_style === 'image' ? 'image' : 'text';
  const nav_title_color = raw.nav_title_color != null ? str(raw.nav_title_color) : undefined;

  return {
    nav_title: str(raw.nav_title),
    nav_title_color,
    badge: normalizeBadge(raw.badge),
    display_style,
    list,
    sort: index + 1,
    navShow: false,
  };
}

function normalizeResourceSlots(slots: unknown): NavResourceSlot[] | undefined {
  if (!Array.isArray(slots) || slots.length === 0) {
    return undefined;
  }
  return slots.map((row, i) => {
    const r = isRecord(row) ? row : {};
    return {
      sort: i + 1,
      resource_image: str(r.resource_image),
      title: r.title != null ? str(r.title) : undefined,
      description: r.description != null ? str(r.description) : undefined,
      jump_target: normalizeJumpTarget(r.jump_target),
      alt: r.image_alt != null ? str(r.image_alt) : undefined,
    };
  });
}

function normalizeFirstLevel(raw: Record<string, unknown>): NavFirstLevelItem {
  const children = raw.children;
  const list: NavSecondLevelItem[] | undefined = Array.isArray(children)
    ? children.map((c, i) => normalizeSecondLevel(isRecord(c) ? c : {}, i))
    : undefined;

  const nav_title_color = raw.nav_title_color != null ? str(raw.nav_title_color) : undefined;

  return {
    nav_title: str(raw.nav_title),
    nav_title_color,
    jump_target: normalizeJumpTarget(raw.jump_target),
    badge: normalizeBadge(raw.badge),
    resource_slots_layout: raw.resource_slots_layout as NavFirstLevelItem['resource_slots_layout'],
    resource_slots: normalizeResourceSlots(raw.resource_slots),
    list,
    navShow: false,
  };
}

function normalizeDataInfoList(items: unknown[]): NavFirstLevelItem[] {
  return items.map((item) => normalizeFirstLevel(isRecord(item) ? item : {}));
}

/**
 * BFF block 302：结构对齐 `nav.json`，输出字段与接口同名（仅结构映射 children/data_info → list）。
 */
export function normalizeBffTopNav(
  topNav: BffPcGlobalData['top_nav'] | null | undefined,
): NavFirstLevelItem[] | null {
  if (topNav == null) {
    return null;
  }

  const rows: unknown[] = Array.isArray(topNav)
    ? topNav
    : Array.isArray(topNav.data_info)
      ? topNav.data_info
      : [];

  if (rows.length === 0) {
    return null;
  }

  const mapped = normalizeDataInfoList(rows);
  return mapped.length ? mapped : null;
}
